import { Element, BarElement, } from 'chart.js';
import { geoContains } from 'd3-geo';
function growGeoBounds(bounds, amount) {
    return [
        [bounds[0][0] - amount, bounds[0][1] - amount],
        [bounds[1][0] + amount, bounds[1][1] + amount],
    ];
}
export class GeoFeature extends Element {
    constructor() {
        super(...arguments);
        this.cache = undefined;
    }
    inRange(mouseX, mouseY) {
        const bb = this.getBounds();
        const r = (Number.isNaN(mouseX) || (mouseX >= bb.x && mouseX <= bb.x2)) &&
            (Number.isNaN(mouseY) || (mouseY >= bb.y && mouseY <= bb.y2));
        const projection = this.projectionScale.geoPath.projection();
        if (r && !Number.isNaN(mouseX) && !Number.isNaN(mouseY) && typeof projection.invert === 'function') {
            const longLat = projection.invert([mouseX, mouseY]);
            return longLat != null && geoContains(this.feature, longLat);
        }
        return r;
    }
    inXRange(mouseX) {
        return this.inRange(mouseX, Number.NaN);
    }
    inYRange(mouseY) {
        return this.inRange(Number.NaN, mouseY);
    }
    getCenterPoint() {
        if (this.cache && this.cache.center) {
            return this.cache.center;
        }
        let center;
        if (this.center) {
            const p = this.projectionScale.projection([this.center.longitude, this.center.latitude]);
            center = {
                x: p[0],
                y: p[1],
            };
        }
        else {
            const centroid = this.projectionScale.geoPath.centroid(this.feature);
            center = {
                x: centroid[0],
                y: centroid[1],
            };
        }
        this.cache = { ...(this.cache || {}), center };
        return center;
    }
    getBounds() {
        if (this.cache && this.cache.bounds) {
            return this.cache.bounds;
        }
        const bb = growGeoBounds(this.projectionScale.geoPath.bounds(this.feature), this.options.borderWidth / 2);
        const bounds = {
            x: bb[0][0],
            x2: bb[1][0],
            y: bb[0][1],
            y2: bb[1][1],
            width: bb[1][0] - bb[0][0],
            height: bb[1][1] - bb[0][1],
        };
        this.cache = { ...(this.cache || {}), bounds };
        return bounds;
    }
    _drawInCache(doc) {
        const bounds = this.getBounds();
        if (!Number.isFinite(bounds.x)) {
            return;
        }
        const canvas = this.cache && this.cache.canvas ? this.cache.canvas : doc.createElement('canvas');
        const x1 = Math.floor(bounds.x);
        const y1 = Math.floor(bounds.y);
        const x2 = Math.ceil(bounds.x + bounds.width);
        const y2 = Math.ceil(bounds.y + bounds.height);
        const pixelRatio = this.pixelRatio || 1;
        const width = Math.ceil(Math.max(x2 - x1, 1) * pixelRatio);
        const height = Math.ceil(Math.max(y2 - y1, 1) * pixelRatio);
        if (width <= 0 || height <= 0) {
            return;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            ctx.scale(pixelRatio, pixelRatio);
            ctx.translate(-x1, -y1);
            this._drawImpl(ctx);
            ctx.restore();
            this.cache = { ...(this.cache || {}), canvas, canvasKey: this._optionsToKey() };
        }
    }
    _optionsToKey() {
        const { options } = this;
        return `${options.backgroundColor};${options.borderColor};${options.borderWidth};${this.pixelRatio}`;
    }
    _drawImpl(ctx) {
        const { feature } = this;
        const { options } = this;
        ctx.beginPath();
        this.projectionScale.geoPath.context(ctx)(feature);
        if (options.backgroundColor) {
            ctx.fillStyle = options.backgroundColor;
            ctx.fill();
        }
        if (options.borderColor) {
            ctx.strokeStyle = options.borderColor;
            ctx.lineWidth = options.borderWidth;
            ctx.stroke();
        }
    }
    draw(ctx) {
        const { feature } = this;
        if (!feature) {
            return;
        }
        if (!this.cache || this.cache.canvasKey !== this._optionsToKey()) {
            this._drawInCache(ctx.canvas.ownerDocument);
        }
        const bounds = this.getBounds();
        if (this.cache && this.cache.canvas && this.cache.canvas.width > 0 && this.cache.canvas.height > 0) {
            const x1 = Math.floor(bounds.x);
            const y1 = Math.floor(bounds.y);
            const x2 = Math.ceil(bounds.x + bounds.width);
            const y2 = Math.ceil(bounds.y + bounds.height);
            const width = x2 - x1;
            const height = y2 - y1;
            if (width > 0 && height > 0) {
                ctx.drawImage(this.cache.canvas, x1, y1, x2 - x1, y2 - y1);
            }
        }
        else if (Number.isFinite(bounds.x)) {
            ctx.save();
            this._drawImpl(ctx);
            ctx.restore();
        }
    }
}
GeoFeature.id = 'geoFeature';
GeoFeature.defaults = {
    ...BarElement.defaults,
    outlineBackgroundColor: null,
    outlineBorderWidth: 0,
    graticuleBorderColor: '#CCCCCC',
    graticuleBorderWidth: 0,
};
GeoFeature.defaultRoutes = {
    outlineBorderColor: 'borderColor',
    ...(BarElement.defaultRoutes || {}),
};
//# sourceMappingURL=GeoFeature.js.map